import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  selectFinancialYear,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { getMdfActivitiesLastSyncTime, isMdfActivitiesSyncRequired } from '../lib'
import {
  requestMdfActivitiesData,
  selectIsMdfActivitiesFetching,
  selectMdfActivitiesLoadedContext,
} from '../model'

const MDF_ACTIVITIES_AUTO_SYNC_ENABLED = false

interface IMdfActivitiesRefreshOptions {
  fYear?: number
  programType?: ProgramTypes
  skip?: boolean
}

export const useMdfActivitiesRefresh = (
  partnerId: string,
  {
    fYear: fYearOverride,
    programType: programTypeOverride,
    skip = false,
  }: IMdfActivitiesRefreshOptions = {}
) => {
  const dispatch = useAppDispatch()
  const configuredFYear = useSelector(selectFinancialYear)
  const configuredProgramType = useSelector(selectProgramType)
  const fYear = fYearOverride ?? configuredFYear
  const programType = programTypeOverride ?? configuredProgramType
  const isRefreshing = useSelector(selectIsMdfActivitiesFetching)
  const loadedContext = useSelector(selectMdfActivitiesLoadedContext)

  const isLoadedForCurrentContext =
    loadedContext?.partnerId === partnerId &&
    loadedContext.fYear === fYear &&
    loadedContext.programType === programType
  const automaticRequestKeyRef = useRef<string | null>(null)

  const readLastSyncTime = useCallback(
    () => getMdfActivitiesLastSyncTime({ partnerId, fYear }),
    [partnerId, fYear]
  )
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(readLastSyncTime)

  const requestMdfActivities = useCallback(
    async (syncBeforeLoad: boolean) => {
      await dispatch(requestMdfActivitiesData({ partnerId, fYear, programType, syncBeforeLoad }))
      setLastSyncTime(readLastSyncTime())
    },
    [dispatch, partnerId, fYear, programType, readLastSyncTime]
  )

  const refreshMdfActivities = useCallback(() => requestMdfActivities(true), [requestMdfActivities])
  const loadMdfActivities = useCallback(() => requestMdfActivities(false), [requestMdfActivities])

  useEffect(() => {
    const storedLastSyncTime = readLastSyncTime()
    const syncRequired =
      MDF_ACTIVITIES_AUTO_SYNC_ENABLED && isMdfActivitiesSyncRequired(storedLastSyncTime)
    const shouldLoad = !isLoadedForCurrentContext

    setLastSyncTime(storedLastSyncTime)

    if (skip || !partnerId || !fYear || isRefreshing || (!shouldLoad && !syncRequired)) {
      return
    }

    const automaticRequestKey = `${partnerId}:${fYear}:${programType}:${storedLastSyncTime ?? 'missing'}`

    if (automaticRequestKeyRef.current === automaticRequestKey) {
      return
    }

    automaticRequestKeyRef.current = automaticRequestKey
    void requestMdfActivities(syncRequired)
  }, [
    partnerId,
    fYear,
    programType,
    isRefreshing,
    isLoadedForCurrentContext,
    skip,
    readLastSyncTime,
    requestMdfActivities,
  ])

  return {
    isRefreshing,
    isLoadedForCurrentContext,
    lastSyncTime,
    refreshMdfActivities,
    loadMdfActivities,
  }
}
